<template>
  <div ref="dropdownRef" class="relative">
    <button
      @click="toggle"
      @keydown.escape="close"
      class="flex items-center gap-1 px-3 py-2 rounded-md hover:bg-accent hover:text-accent-foreground transition-colors duration-150"
      :class="[triggerClass]"
      :aria-expanded="isOpen"
      aria-haspopup="true"
    >
      <slot name="trigger">
        <span>{{ label }}</span>
      </slot>
      <svg
        class="w-4 h-4 transition-transform duration-150"
        :class="{ 'rotate-180': isOpen }"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
    </button>

    <Transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="opacity-0 scale-95"
      enter-to-class="opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-95"
    >
      <div
        v-show="isOpen"
        class="absolute z-50 mt-1 min-w-[8rem] bg-card rounded-md border border-border p-1"
        :class="[alignClass, menuClass]"
        @keydown.escape="close"
      >
        <slot @close="close" />
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue'

const props = defineProps({
  label: {
    type: String,
    default: ''
  },
  align: {
    type: String,
    default: 'left',
    validator: (v) => ['left', 'right'].includes(v)
  },
  triggerClass: {
    type: String,
    default: ''
  },
  menuClass: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['open', 'close'])

const isOpen = ref(false)
const dropdownRef = ref(null)

const alignClass = props.align === 'right' ? 'right-0' : 'left-0'

const toggle = () => {
  isOpen.value = !isOpen.value
}

const close = () => {
  isOpen.value = false
}

const open = () => {
  isOpen.value = true
}

const handleClickOutside = (e) => {
  if (dropdownRef.value && !dropdownRef.value.contains(e.target)) {
    close()
  }
}

const handleKeydown = (e) => {
  if (e.key === 'Escape' && isOpen.value) {
    close()
  }
}

watch(isOpen, (val) => {
  if (val) {
    emit('open')
  } else {
    emit('close')
  }
})

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  document.removeEventListener('keydown', handleKeydown)
})

defineExpose({ close, open, toggle })
</script>
